import { STORAGE_KEY_ACCESS_TOKEN } from '../../const';
import { RequestsApi } from '../RequestsApi';
import storageService from '../StorageService';

class AuthApi extends RequestsApi {
    async getUser() {
        return await this.request('/user', 'GET');
    }
    async updateUser(props) {
        return await this.request('/user', 'post', props);
    }

    async deleteUser() {
        return await this.request('/user', 'delete');
    }

    async getUserLocation(search) {
        // HORO-10360: the birth chart flow collects a birthplace BEFORE the
        // sign-up wall, and /user/location needs a signed-in user. Guests use
        // the public, rate-limited autocomplete the birth chart widget already
        // runs on, reshaped to the same `{ data: { data: [...] } }` envelope.
        if (!storageService.get(STORAGE_KEY_ACCESS_TOKEN)) {
            const response = await this.request('/birth-chart/places/autocomplete', 'post', { input: search });
            return { ...response, data: { data: response?.data?.predictions || [] } };
        }
        return await this.request('/user/location', 'post', { input: search });
    }

    // HORO-10360: birth chart. POST builds (and saves the details to the
    // profile); GET reads the latest report, rebuilt server-side if the profile
    // changed. 404 means "no report yet" (or the feature is off).
    async generateBirthChart(body) {
        return await this.request('/user/birth-chart', 'post', body);
    }

    async getBirthChart() {
        return await this.request('/user/birth-chart', 'get');
    }

    // HORO-10647: saved tarot readings, newest first. Not premium-gated.
    async getTarotReadings({ variant = 'love', page = 1 } = {}) {
        return await this.request(`/user/tarot-readings?variant=${variant}&page=${page}`, 'get');
    }

    // HORO-10647: thumbs up/down on a saved reading; `null` clears the vote.
    async setTarotReadingFeedback(id, feedback) {
        // X-FE-Handle-Errors: the Love Tarot tab shows its own error / disabled state.
        return await this.request(`/user/tarot-readings/${id}/feedback`, 'put', { feedback }, { 'X-FE-Handle-Errors': '1' });
    }

    // HORO-10360: the user opened their chart. The report is often already in
    // the browser, so the server can't see opens from GET /user/birth-chart.
    async trackBirthChartView(surface) {
        return await this.request('/user/birth-chart/view', 'post', { surface });
    }

    // Public env flags — anonymous visitors never load GET /user.
    async getFeatures() {
        return await this.request('/features', 'get');
    }

    async userLogout() {
        return await this.request('/user/logout', 'post');
    }

    // HORO-10509: email/password signup, signin, and password reset. All
    // hit the new unauthenticated /auth/* endpoints except addEmail/
    // verifyAddedEmail, which rely on RequestsApi already attaching the
    // Bearer token for a logged-in (OAuth) user.
    async emailSignUp(email, registrationSource) {
        return await this.request('/auth/signup', 'post', { email, ...(registrationSource ? { registrationSource } : {}) });
    }

    async emailSetPassword(email, password, confirmPassword) {
        return await this.request('/auth/set-password', 'post', { email, password, confirmPassword });
    }

    async emailVerify(email, code) {
        return await this.request('/auth/verify-email', 'post', { email, code });
    }

    async emailResendCode(email, purpose) {
        return await this.request('/auth/resend-code', 'post', { email, purpose });
    }

    async emailSignIn(email, password) {
        return await this.request('/auth/signin', 'post', { email, password });
    }

    async emailForgotPassword(email) {
        return await this.request('/auth/forgot-password', 'post', { email });
    }

    // HORO-10509: checks the 6-digit code on its own, ahead of and
    // independently of setting the password — see authSlice's
    // emailVerifyResetCode. Returns a `resetToken` that replaces the raw
    // code in emailResetPassword below.
    async verifyResetCode(email, code) {
        return await this.request('/auth/verify-reset-code', 'post', { email, code });
    }

    async emailResetPassword(email, resetToken, password, confirmPassword) {
        return await this.request('/auth/reset-password', 'post', { email, resetToken, password, confirmPassword });
    }

    async addEmail(email) {
        return await this.request('/auth/add-email', 'post', { email });
    }

    async verifyAddedEmail(code) {
        return await this.request('/auth/verify-added-email', 'post', { code });
    }
}

export default new AuthApi();
